import { CheckBox, Spacer, VDBText } from "@components/atoms";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components/native";
import { Image } from "expo-image";
import { SpaceType } from "@components/atoms/Spacer/types";
import LabelView from "../LabelView";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { Pressable, View } from "react-native";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { GlobalEnum, ProductCategory } from "@utils/constants";
import EmptyImagePreview from "../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "../GalleryView/utils/EmptyImage/utils";
import { useItemDetailsCardSubSections } from "../../../hooks/useSortCustomData";
import {
  getSubtitlePriceModes,
  isPair,
  isMeleeItem,
} from "@utils/product/productValidators";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import { getOptimizedListImageUrl, isImageUrlEmptyOrUndefined } from "../Utils";
import { formatItemValue, ImageComponentWithSummaryTypes } from "./types";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import { useSelector } from "react-redux";
import { showToast, ToastTypes } from "../VDBToast/util";
import { RequestTypes } from "@scenes/requests/utils/utils";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { StoneType } from "@scenes/bulk-request/Utils";
import { css } from "styled-components/native";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";

const ImageComponentWithSummary = ({
  mItem,
  isCheckboxVisible = false,
  checkBoxSelection,
  ismItemChecked = false,
  productType,
  showAvailabilityDot,
  fromBulkList = false,
  showListPrice,
  isTotalOfferDiscountApplied,
  requestType,
  hasAnyOfferDetails,
  stockItemRequest,
  fromSearchResult = false,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  stoneType = StoneType.SINGLE_STONES,
}: ImageComponentWithSummaryTypes) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const priceMode = userInfo?.user?.price_mode;
  const { t } = useTranslation();
  const userCurrency = useUserCurrency();
  const isCustomApp = useSelector((state) => state?.appState?.isCustomApp);
  const { deviceWidth } = useIsFoldable();
  const screenWidth = deviceWidth - (theme.isMobile ? 24 : 32);
  const typography = useTypographyStore();
  const [imageUri, setImageUri] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
      ? getOptimizedListImageUrl(mItem)
      : FALLBACK_IMAGE_ASSET
  );
  const isTotalOfferPriceApplied =
    stockItemRequest?.quote_details?.total_offer_price_applied;
  const [isItemChecked, setIsItemChecked] = useState(false);
  const [showImageContainer, setShowImageContainer] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
  );
  const [showErrorImage, setShowErrorImage] = useState(
    isImageUrlEmptyOrUndefined(mItem)
  );
  const { cpsViewDataOptions, showStockNum } =
    useItemDetailsCardSubSections(
      productType as ProductCategory,
      priceMode,
      sectionName,
      fromBulkList,
      stoneType
    ) || {};

  const checkOfferApplied = () => {
    if (!stockItemRequest) return false;
    const itemTotal = Object.values(stockItemRequest?.children).reduce(
      (sum, item: any) =>
        sum +
        Number(
          item?.updated_total_sales_price || item?.offer_total_sales_price
        ),
      0
    );
    return (
      formatNumberWithPrecision(
        Number(stockItemRequest?.offer_total_sales_price),
        { precision: userInfo?.roundUpPrices() && !isMeleeItem(mItem) ? 0 : 2 }
      )?.replaceAll(",", "") !==
      formatNumberWithPrecision(itemTotal, {
        precision: userInfo?.roundUpPrices() && !isMeleeItem(mItem) ? 0 : 2,
      })?.replaceAll(",", "")
    );
  };

  const Wrapper =
    (isTotalOfferPriceApplied || checkOfferApplied()) &&
    isTotalOfferDiscountApplied === false &&
    !hasAnyOfferDetails
      ? Pressable
      : View;

  const showTopDetailsData = useMemo(() => {
    const customPriceMode = getSubtitlePriceModes(cpsViewDataOptions);
    if (customPriceMode && Array.isArray(customPriceMode)) {
      if (!customPriceMode.includes(priceMode)) {
        return false;
      }
    }
    return true;
  }, [priceMode, cpsViewDataOptions]);

  const checkStateChanged = (checked: boolean) => {
    if (
      !fromBulkList &&
      stockItemRequest &&
      stockItemRequest?.status !== "pending"
    ) {
      return;
    }
    if (
      stockItemRequest?.additional_attributes?.total_offer_price_applied ||
      requestType === RequestTypes.Buy ||
      requestType === RequestTypes.Quote
    ) {
      if (
        (isTotalOfferPriceApplied || checkOfferApplied()) &&
        isTotalOfferDiscountApplied === false &&
        !hasAnyOfferDetails
      ) {
        showToast(
          "",
          requestType === RequestTypes.Quote
            ? t("requestHistoryDetail.quote.quoteWarning.text")
            : t("requestHistoryDetail.buy.buyWarning.text"),
          ToastTypes.ERROR,
          true,
          true,
          80
        );
        return;
      }
    }
    setIsItemChecked(checked);
    checkBoxSelection(checked, mItem);
  };

  useEffect(() => {
    setIsItemChecked(ismItemChecked);
  }, [ismItemChecked]);

  useEffect(() => {
    setIsItemChecked(mItem?.is_selected ?? false);
  }, [mItem?.is_selected]);

  const getAvailabilityStatus = () => {
    switch (mItem?.available) {
      case GlobalEnum.OUT_ON_MEMO:
        return "error-01";
      case GlobalEnum.ON_HAND:
        return "success-01";
      case GlobalEnum.PLEASE_EMAIL:
      default:
        return "warn-01";
    }
  };

  return (
    <ImageAndContentContainer>
      <ImageAndCheckboxContainer isCheckboxVisible={isCheckboxVisible}>
        {isCheckboxVisible && !userInfo?.isIFrame() ? (
          <Wrapper
            onPress={
              (isTotalOfferPriceApplied || checkOfferApplied()) &&
              isTotalOfferDiscountApplied === false &&
              !hasAnyOfferDetails
                ? checkStateChanged
                : undefined
            }
          >
            <View
              style={{
                pointerEvents:
                  showListPrice || fromSearchResult ? "auto" : "none",
              }}
            >
              <CheckBox
                fillColor={theme["knob-bor"]}
                checkBoxSize={20}
                checkedFunction={checkStateChanged}
                isChecked={isItemChecked}
                disabled={
                  (isTotalOfferPriceApplied || checkOfferApplied()) &&
                  isTotalOfferDiscountApplied === false &&
                  !hasAnyOfferDetails
                    ? true
                    : false
                }
              />
            </View>
          </Wrapper>
        ) : null}
        <ImageResourceContainer>
          {showErrorImage ? (
            <FallbackImageContainer theme={theme}>
              <EmptyImagePreview
                shape={getShapeValue(mItem)}
                size={30}
                isPair={isPair(mItem) ?? false}
              />
            </FallbackImageContainer>
          ) : showImageContainer ? (
            <Image
              style={{
                height: 48,
                width: 48,
                borderRadius: 8,
                borderColor: theme["core-03"],
                borderWidth: 1,
              }}
              source={getImageSource(
                imageUri || FALLBACK_IMAGE_ASSET,
                ImageSizePreset.LIST_THUMB
              )}
              contentFit={userInfo.disableImageZoom() ? "contain" : "cover"}
              {...getIOSImageProps()}
              onError={() => {
                if (mItem?.video_url) {
                  setShowImageContainer(false);
                } else {
                  setShowErrorImage(true);
                  setShowImageContainer(false);
                  setImageUri(FALLBACK_IMAGE_ASSET);
                }
              }}
            />
          ) : (
            <VideoBackgroundContainer theme={theme}>
              <VideoPlayIconContainer theme={theme}>
                <VDBIcon
                  color={theme["core-06"]}
                  size={20}
                  variant={IconsVariant.VDBCustom}
                  name={VDBIcons.PLAY}
                />
              </VideoPlayIconContainer>
            </VideoBackgroundContainer>
          )}
        </ImageResourceContainer>
        {showAvailabilityDot && (
          <AvailabilityDot bgColor={theme[getAvailabilityStatus()]} />
        )}
      </ImageAndCheckboxContainer>
      <FirstContentContainer>
        {mItem?.short_title && (
          <>
            <VDBText
              type={typography["Minor-M"]}
              color={theme["core-05"]}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {mItem?.short_title}
            </VDBText>
            <Spacer size={SpaceType.ExtraSmall} />
          </>
        )}
        {sectionName !== SearchFilterTypeEnum.PARCEL && (
          <FirstContentMapContainer>
            {showTopDetailsData &&
              !isPair(mItem) &&
              cpsViewDataOptions?.map((item: any, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <LabelView
                      itemLeftText={t(`${item.label}`)}
                      itemRightText={formatItemValue(
                        isCustomApp,
                        item.response_key,
                        mItem,
                        userCurrency,
                        userInfo,
                        t
                      )}
                      leftPostfixText=":"
                      keyTypographyStyle={typography["Minor-R"]}
                      valueTypographyStyle={typography["Minor-M"]}
                      allowSpaceAfterLeftText={false}
                    />
                    <Spacer size={SpaceType.ExtraSmall} />
                  </React.Fragment>
                );
              })}
          </FirstContentMapContainer>
        )}
        {mItem?.stock_num && showStockNum && (
          <TitleContainer screenWidth={screenWidth}>
            <Spacer size={SpaceType.ExtraSmall} />
            <TextContainer>
              <VDBText
                type={typography["Minor-R"]}
                color={theme["core-06"]}
                ellipsizeMode="tail"
              >
                {t("searchResult.listView.stockNum.text")}:
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
              <VDBText
                type={{ ...typography["Minor-M"], maxWidth: "90%" }}
                color={theme["core-05"]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {mItem?.stock_num}
              </VDBText>
            </TextContainer>
          </TitleContainer>
        )}
      </FirstContentContainer>
    </ImageAndContentContainer>
  );
};

const FirstContentContainer = styled.View`
  margin-start: 4px;
  justify-content: center;
  width: ${({ theme }) => (theme.isLargeScreen ? 85 : 65)}%;
`;

const FirstContentMapContainer = styled.View`
  flex-direction: row;
`;

const ImageAndCheckboxContainer = styled.View<{
  isCheckboxVisible?: boolean;
}>`
  height: 76px;
  padding-left: ${({ isCheckboxVisible }) =>
    isCheckboxVisible ? "0px" : "8px"};
  padding-right: ${({ isCheckboxVisible }) =>
    isCheckboxVisible ? "0px" : "8px"};
  ${({ isCheckboxVisible }) =>
    isCheckboxVisible &&
    css`
      width: 84px;
    `}
  background-color: ${({ theme }) => theme["core-01"]};
  border-top-left-radius: 8px;
  justify-content: center;
  align-items: center;
  flex-direction: row;
`;

const ImageAndContentContainer = styled.View<any>`
  flex-direction: row;
`;

const ImageResourceContainer = styled.View`
  padding-left: 2px;
`;

const TitleContainer = styled.View<any>``;

const AvailabilityDot = styled.View<any>`
  height: 8px;
  width: 8px;
  border-radius: 4px;
  border: 1px;
  border-color: ${({ theme }) => theme["cont-00"]};
  background-color: ${({ bgColor }) => bgColor};
  position: absolute;
  top: ${({ theme }) => (theme.isMobile ? "54px" : "55px")};
  left: ${({ theme }) => (theme.isMobile ? "36px" : "10px")};
`;

const VideoPlayIconContainer = styled.View<any>`
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${({ theme }) => theme["over-02"]};
  border-radius: 14px;
`;

const VideoBackgroundContainer = styled.View<any>`
  height: 48px;
  width: 48px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const FallbackImageContainer = styled.View<any>`
  width: 48px;
  height: 48px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  border-color: ${({ theme }) => theme["core-03"]};
  border-width: 1px;
`;

const TextContainer = styled.View`
  flex-direction: row;
`;

export default React.memo(ImageComponentWithSummary);
